import React from "react"

import type { MetricRecord } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  AnnotationRenderContext,
  WordHighlight,
} from "@/components/transcript/word-highlight"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import type { Message } from "@/types/Transcript"

import {
  TranscriptionErrorDetails,
  type TranscriptionErrorResult,
} from "../../components/TranscriptionErrorDetails"
import { TranscriptionErrorConfig } from "../../configuration/components/TranscriptionErrorConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

interface ErrorSegmentLike {
  error_type?: "insert" | "delete" | "replace"
  reference_error?: string | null
  hypothesis_error?: string | null
  reference_segment?: string | null
}

interface LegacySentenceMismatch {
  message_index?: number | null
  role?: string | null
  reference_text?: string
  hypothesis_text?: string
  error_segments?: ErrorSegmentLike[]
}

function getErrorMetadata(
  subvalue: SubvalueByTimestamp,
): ErrorSegmentLike | null {
  const metadata = subvalue.metadata
  if (!metadata || typeof metadata !== "object") {
    return null
  }
  return metadata as ErrorSegmentLike
}

function readLegacySentenceMismatches(
  mo: Pick<RunMetricOutput, "result_json">,
): LegacySentenceMismatch[] | null {
  const mismatches = (
    mo.result_json as { sentence_mismatches?: LegacySentenceMismatch[] } | null
  )?.sentence_mismatches
  return mismatches?.length ? mismatches : null
}

function readTranscriptionErrorResult(
  mo: Pick<RunMetricOutput, "result" | "result_json">,
): TranscriptionErrorResult | null {
  // Preferred: new typed NumericalResult.
  const result = mo.result as
    | {
        count?: number
        frequency?: number
        raw_values?: {
          divergent_sections_count?: number
          reference_word_count?: number
          hypothesis_word_count?: number
          role?: "agent" | "persona" | "both"
        }
      }
    | null
    | undefined
  const rawValues = result?.raw_values
  if (
    result?.frequency != null &&
    rawValues?.reference_word_count != null &&
    rawValues?.hypothesis_word_count != null
  ) {
    return {
      wer: result.frequency,
      divergent_sections_count: rawValues.divergent_sections_count ?? 0,
      reference_word_count: rawValues.reference_word_count,
      hypothesis_word_count: rawValues.hypothesis_word_count,
      role: rawValues.role,
    }
  }

  // Deprecated fallback: legacy result_json shape.
  const legacy = mo.result_json as
    | {
        wer?: number
        divergent_sections_count?: number
        reference_word_count?: number
        hypothesis_word_count?: number
      }
    | null
    | undefined
  if (legacy?.wer != null) {
    return {
      wer: legacy.wer,
      divergent_sections_count: legacy.divergent_sections_count ?? 0,
      reference_word_count: legacy.reference_word_count ?? 0,
      hypothesis_word_count: legacy.hypothesis_word_count ?? 0,
    }
  }

  return null
}

export class TranscriptionErrorMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "%"
  aggregate_units = "%"
  metricScale = 100

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (!mo.subvalues_by_timestamp?.length) {
      return []
    }

    const errorColors: Record<string, string> = {
      replace: "220, 38, 38",
      delete: "234, 88, 12",
    }

    // Inserts are zero-width transcript-only markers — they have no audio
    // anchor, so they're skipped here to keep the waveform clean.
    return mo.subvalues_by_timestamp
      .filter((sv) => getErrorMetadata(sv)?.error_type !== "insert")
      .map((sv, i) => {
        const metadata = getErrorMetadata(sv)
        const errorType = metadata?.error_type ?? "replace"
        const confidence = sv.float_value ?? 0.5
        const rgb = errorColors[errorType] ?? errorColors.replace
        const opacity = Math.min(0.3 + (1 - confidence) * 0.7, 0.9)

        return {
          id: `transcription-error-${i}`,
          start: sv.start_offset,
          end: sv.end_offset,
          color: `rgba(${rgb}, ${opacity})`,
          drag: false,
          resize: false,
        }
      })
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const result = readTranscriptionErrorResult(mo)
    if (!result) {
      return null
    }
    return <TranscriptionErrorDetails result={result} />
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    // super (WithAudioFilterMixin) appends the shared audio-scope editor, which
    // now drives role selection for this metric.
    return (
      <div className="flex flex-col gap-4">
        <TranscriptionErrorConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(
          metricRecordData,
          onChange,
          textAreaRef,
          editorContext,
        )}
      </div>
    )
  }

  /**
   * Find the character offset of a word or phrase within text.
   * Tries exact substring match first, then falls back to word-boundary matching.
   */
  private findWordOffset(
    text: string,
    word: string,
  ): { start: number; end: number } | null {
    if (!word || !text) {
      return null
    }
    const normalizedText = text.toLowerCase()
    const normalizedWord = word.toLowerCase().trim()

    const index = normalizedText.indexOf(normalizedWord)
    if (index !== -1) {
      return { start: index, end: index + word.length }
    }

    // Word-boundary fallback for tokenized matches.
    const words = normalizedText.split(/\s+/)
    const searchWords = normalizedWord.split(/\s+/)
    let charOffset = 0

    for (let i = 0; i < words.length; i++) {
      const wordStart = charOffset
      const wordEnd = charOffset + words[i].length

      if (
        words[i].includes(searchWords[0]) ||
        searchWords[0].includes(words[i])
      ) {
        const matchStart = wordStart
        let matchEnd = wordEnd
        let searchWordIndex = 1
        let textWordIndex = i + 1

        while (
          searchWordIndex < searchWords.length &&
          textWordIndex < words.length
        ) {
          if (
            words[textWordIndex].includes(searchWords[searchWordIndex]) ||
            searchWords[searchWordIndex].includes(words[textWordIndex])
          ) {
            let endCharOffset = charOffset
            for (let j = i; j <= textWordIndex; j++) {
              if (j > i) {
                endCharOffset += 1
              }
              endCharOffset += words[j].length
            }
            matchEnd = endCharOffset
            searchWordIndex++
            textWordIndex++
          } else {
            break
          }
        }

        if (searchWordIndex >= searchWords.length) {
          return { start: matchStart, end: matchEnd }
        }
      }

      charOffset = wordEnd + 1
    }

    return null
  }

  /**
   * Build a single WordHighlight from one error segment, given the message
   * content it lives in. Shared between the new (subvalue) and legacy
   * (sentence_mismatches) data paths.
   */
  private buildHighlightForSegment(
    messageContent: string,
    messageIndex: number,
    segment: ErrorSegmentLike,
  ): WordHighlight | null {
    const errorType = segment.error_type
    if (!errorType) {
      return null
    }

    if (errorType === "insert") {
      // The "hypothesis" is the customer transcript, which IS the message
      // content displayed in the UI. The inserted word lives inside it, so
      // find it directly to get a positive-width highlight. Falling back to
      // a zero-width anchor based on reference_segment loses inserts in the
      // merge step inside AnnotatableMessage.
      const hypothesisError = segment.hypothesis_error ?? undefined
      if (hypothesisError) {
        const offset = this.findWordOffset(messageContent, hypothesisError)
        if (
          offset &&
          offset.start < offset.end &&
          offset.end <= messageContent.length
        ) {
          return {
            messageIndex,
            startOffset: offset.start,
            endOffset: offset.end,
            errorType: "insert",
            hypothesisError,
          }
        }
      }

      const referenceSegment = segment.reference_segment
      if (!referenceSegment) {
        return null
      }
      const messageLower = messageContent.toLowerCase()
      const lastContextWords = referenceSegment
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .slice(-3)
        .join(" ")
      const contextIndex =
        lastContextWords.length > 0
          ? messageLower.indexOf(lastContextWords)
          : -1
      if (contextIndex === -1) {
        return null
      }
      const insertPosition = contextIndex + lastContextWords.length
      return {
        messageIndex,
        startOffset: insertPosition,
        endOffset: insertPosition,
        errorType: "insert",
        hypothesisError,
      }
    }

    if (errorType === "delete") {
      if (!segment.reference_error) {
        return null
      }
      const offset = this.findWordOffset(
        messageContent,
        segment.reference_error,
      )
      if (
        !offset ||
        offset.start >= offset.end ||
        offset.end > messageContent.length
      ) {
        return null
      }
      return {
        messageIndex,
        startOffset: offset.start,
        endOffset: offset.end,
        errorType: "delete",
        missingWord: segment.reference_error,
      }
    }

    if (errorType === "replace" && segment.hypothesis_error) {
      const offset = this.findWordOffset(
        messageContent,
        segment.hypothesis_error,
      )
      if (
        !offset ||
        offset.start >= offset.end ||
        offset.end > messageContent.length
      ) {
        return null
      }
      return {
        messageIndex,
        startOffset: offset.start,
        endOffset: offset.end,
        errorType: "substitute",
        hypothesisError: segment.hypothesis_error,
        missingWord: segment.reference_error ?? undefined,
      }
    }

    return null
  }

  /**
   * Legacy text-similarity match: find the transcript message index whose
   * content best matches a sentence mismatch's reference/hypothesis text.
   * Mirrors the historical implementation that drives prod for already-stored
   * transcription error outputs (subvalues lack message_index in legacy data).
   */
  private findMatchingMessageIndex(
    mismatch: LegacySentenceMismatch,
    transcript: Message[],
  ): number | null {
    const normalize = (text: string): string =>
      text
        .toLowerCase()
        .replace(/[^\w\s]/g, "")
        .replace(/\s+/g, " ")
        .trim()

    const normalizedReference = normalize(mismatch.reference_text ?? "")
    const normalizedHypothesis = normalize(mismatch.hypothesis_text ?? "")

    const transcriptRole =
      mismatch.role === "agent"
        ? "assistant"
        : mismatch.role === "persona"
          ? "user"
          : null
    if (!transcriptRole) {
      return null
    }

    let bestMatch: { index: number; score: number } | null = null

    for (let index = 0; index < transcript.length; index++) {
      const message = transcript[index]
      if (message.role !== transcriptRole) {
        continue
      }
      const content = typeof message.content === "string" ? message.content : ""
      const normalizedContent = normalize(content)

      let score = 0
      if (normalizedContent.includes(normalizedReference)) {
        score += 0.5
      }
      if (normalizedContent.includes(normalizedHypothesis)) {
        score += 0.5
      }

      const contentWords = normalizedContent
        .split(/\s+/)
        .filter((word) => word.length > 2)
      const referenceWords = normalizedReference
        .split(/\s+/)
        .filter((word) => word.length > 2)
      const hypothesisWords = normalizedHypothesis
        .split(/\s+/)
        .filter((word) => word.length > 2)

      const referenceMatch = referenceWords.filter((word) =>
        contentWords.some((cw) => cw.includes(word) || word.includes(cw)),
      ).length
      const hypothesisMatch = hypothesisWords.filter((word) =>
        contentWords.some((cw) => cw.includes(word) || word.includes(cw)),
      ).length

      score +=
        (referenceMatch / Math.max(referenceWords.length, 1)) * 0.25 +
        (hypothesisMatch / Math.max(hypothesisWords.length, 1)) * 0.25

      if (score > 0 && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { index, score }
      }
    }

    return bestMatch && bestMatch.score > 0.3 ? bestMatch.index : null
  }

  getHighlightedMessages(mo: RunMetricOutput): number[] {
    // New path: subvalues with message_index.
    const subvalueIndices =
      mo.subvalues_by_timestamp
        ?.filter((sv) => sv.message_index != null)
        .map((sv) => sv.message_index as number) ?? []
    if (subvalueIndices.length > 0) {
      return [...new Set(subvalueIndices)]
    }

    // Legacy path: result_json.sentence_mismatches.
    const legacyMismatches = readLegacySentenceMismatches(mo)
    if (!legacyMismatches) {
      return []
    }
    return [
      ...new Set(
        legacyMismatches
          .map((mismatch) => mismatch.message_index)
          .filter((index): index is number => index != null),
      ),
    ]
  }

  getHighlightedWords(
    mo: RunMetricOutput,
    transcript: Message[],
  ): WordHighlight[] | null {
    if (!transcript.length) {
      return null
    }

    const highlights: WordHighlight[] = []

    // New path: each subvalue carries its own message_index and metadata.
    const newPathSubvalues =
      mo.subvalues_by_timestamp?.filter((sv) => sv.message_index != null) ?? []
    if (newPathSubvalues.length > 0) {
      for (const subvalue of newPathSubvalues) {
        const messageIndex = subvalue.message_index as number
        const message = transcript[messageIndex]
        const messageContent =
          typeof message?.content === "string" ? message.content : ""
        if (!messageContent) {
          continue
        }
        const segment = getErrorMetadata(subvalue)
        if (!segment) {
          continue
        }
        const highlight = this.buildHighlightForSegment(
          messageContent,
          messageIndex,
          segment,
        )
        if (highlight) {
          highlights.push(highlight)
        }
      }
      return highlights.length > 0 ? highlights : null
    }

    // Legacy path: read from result_json.sentence_mismatches. Subvalues from
    // the old backend don't carry message_index, so we fall through to the
    // sentence-mismatch payload and reuse the historical text-similarity
    // matcher when a mismatch lacks an explicit message_index.
    const legacyMismatches = readLegacySentenceMismatches(mo)
    if (!legacyMismatches) {
      return null
    }
    for (const mismatch of legacyMismatches) {
      const messageIndex =
        mismatch.message_index ??
        this.findMatchingMessageIndex(mismatch, transcript)
      if (messageIndex == null) {
        continue
      }
      const message = transcript[messageIndex]
      const messageContent =
        typeof message?.content === "string" ? message.content : ""
      if (!messageContent) {
        continue
      }
      for (const segment of mismatch.error_segments ?? []) {
        const highlight = this.buildHighlightForSegment(
          messageContent,
          messageIndex,
          segment,
        )
        if (highlight) {
          highlights.push(highlight)
        }
      }
    }
    return highlights.length > 0 ? highlights : null
  }

  renderAnnotation(
    context: AnnotationRenderContext,
    key: string,
  ): React.ReactNode | null {
    const { text, highlight } = context

    if (!highlight.errorType) {
      return null
    }

    const { errorType, hypothesisError } = highlight

    if (errorType === "insert") {
      const displayText = text || hypothesisError
      if (!displayText) {
        return null
      }
      return (
        <span
          key={key}
          className="bg-blue-300 dark:bg-blue-800 text-blue-900 dark:text-blue-100 px-1.5 py-0.5 underline rounded-sm"
        >
          {displayText}
        </span>
      )
    }

    if (errorType === "delete") {
      return (
        <span
          key={key}
          className="bg-red-300 dark:bg-red-800 text-red-900 dark:text-red-100 line-through px-1.5 py-0.5 rounded-sm"
        >
          {text}
        </span>
      )
    }

    if (errorType === "substitute") {
      return (
        <span
          key={key}
          className="bg-yellow-300 dark:bg-yellow-800 text-yellow-900 dark:text-yellow-100 px-1.5 py-0.5 rounded-sm"
        >
          {text}
        </span>
      )
    }

    return (
      <span
        key={key}
        className="bg-red-300 dark:bg-red-800 text-black dark:text-white px-0.5 rounded-sm"
      >
        {text}
      </span>
    )
  }
}
